import clsx from 'clsx';
import { m, LazyMotion, domAnimation } from 'framer-motion';
import { Link } from 'gatsby';
import PropTypes from 'prop-types';
import React, { useCallback, useContext, useRef, useState } from 'react';

import MainContext from 'context/main';
import useOnClickOutside from 'hooks/use-on-click-outside';

import arrowDownIcon from './images/arrow-down.svg';
import japanFlagIcon from './images/flags/japan.svg';
import ukFlagIcon from './images/flags/uk.svg';

const ANIMATION_DURATION = 0.2;

const dropdownVariants = {
  hidden: { opacity: 0, y: '0', height: 0, pointerEvents: 'none', visibility: 'hidden' },
  visible: {
    opacity: 1,
    y: '0',
    height: 'auto',
    pointerEvents: 'auto',
    visibility: 'visible',
    transition: {
      duration: ANIMATION_DURATION,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      delay: ANIMATION_DURATION,
    },
  },
};

const flags = {
  en: ukFlagIcon,
  ja: japanFlagIcon,
};

const directions = {
  top: 'bottom-0',
  bottom: 'flex flex-col-reverse',
};

const sizeClassName = 'h-10 w-36';

const LangSelect = ({ className, openTo }) => {
  const { pageUrl, language, translations } = useContext(MainContext);
  const [lang, setLang] = useState(language.name);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const languages = [
    {
      name: language.name,
      flag: flags[language.locale],
      uri: pageUrl,
    },
    ...translations.map((translation) => ({
      name: translation.language.name,
      flag: flags[translation.language.locale],
      uri: translation.uri,
    })),
  ];

  const handleClickOutside = useCallback(() => {
    setShowDropdown(false);
  }, [setShowDropdown]);

  useOnClickOutside(dropdownRef, handleClickOutside);

  const handleSelect = (name) => {
    setLang(name);
    setShowDropdown(false);
  };

  const handleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  if (!lang) return null;

  return (
    <div
      className={clsx('relative', openTo === 'bottom' && 'xs:!w-[66px]', sizeClassName, className)}
    >
      <div
        className={clsx(
          'absolute z-40 w-full rounded-sm border border-new-gray-1/25 bg-white text-sm font-sans2 text-new-black leading-none',
          directions[openTo]
        )}
        ref={dropdownRef}
      >
        <LazyMotion features={domAnimation}>
          <m.ul
            className="w-full"
            initial="hidden"
            animate={showDropdown ? 'visible' : 'hidden'}
            variants={dropdownVariants}
          >
            {languages
              .filter(({ name, uri }) => name !== lang && uri !== '/cat/')
              .map(({ name, flag, uri }, index) => (
                <m.li key={index} variants={itemVariants}>
                  <Link
                    className={clsx(
                      'flex items-center !w-full py-[7px] pl-2.5 pr-3 transition-colors duration-200 hover:bg-[rgba(234,237,246,0.5)]',
                      openTo === 'bottom' && 'xs:!w-[66px]',
                      sizeClassName
                    )}
                    to={uri}
                    onClick={() => handleSelect(name)}
                  >
                    <span className={clsx('ml-1 capitalize', openTo === 'bottom' && 'xs:hidden')}>
                      {name}
                    </span>
                    {openTo === 'bottom' && (
                      <img
                        className="xs:inline-block hidden"
                        src={flag}
                        width={26}
                        height={26}
                        loading="lazy"
                        alt=""
                      />
                    )}
                  </Link>
                </m.li>
              ))}
          </m.ul>
        </LazyMotion>
        <button
          className={clsx(
            'flex items-center py-[7px] pl-2.5 pr-3 !w-full',
            openTo === 'bottom' && 'xs:!w-[66px]',
            sizeClassName
          )}
          type="button"
          aria-label="Select language"
          onClick={handleDropdown}
        >
          <span className={clsx('ml-1 capitalize', openTo === 'bottom' && 'xs:sr-only')}>
            {language.name}
          </span>
          {openTo === 'bottom' && (
            <img
              className="xs:inline-block hidden"
              src={flags[language.locale]}
              width={26}
              height={26}
              loading="lazy"
              alt=""
            />
          )}
          <img
            className={clsx(
              'ml-auto transition-all duration-200',
              showDropdown ? 'rotate-180' : 'rotate-0'
            )}
            src={arrowDownIcon}
            width={10}
            height={5}
            loading="lazy"
            alt=""
          />
        </button>
      </div>
    </div>
  );
};

LangSelect.propTypes = {
  className: PropTypes.string,
  openTo: PropTypes.oneOf(Object.keys(directions)).isRequired,
};

LangSelect.defaultProps = {
  className: null,
};

export default LangSelect;
